import React, { useEffect, useRef, useState } from "react";
import { AppBar, Box, IconButton, Stack, Toolbar } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { useLanding } from "../context/LandingContext";
import { appendConfiguredQuery, openConfiguredUrl } from "../utils/configuredNavigation";
import {
  buildWhatsAppUrl,
  fullBusinessAddress,
  resolveLandingMedia,
} from "../utils/landingMedia";
import { hasVisibleLandingSegmentType } from "../utils/landingLayout";
import { catalogLabel, catalogListPath } from "../utils/catalogRouting";
import { isCanonicalGastronomicCatalogNavigation } from "../utils/siteExperienceDefaults";
import NavbarAccountMenu from "./navbar/NavbarAccountMenu";
import NavbarActions from "./navbar/NavbarActions";
import NavbarBrand from "./navbar/NavbarBrand";
import NavbarDesktopNavigation from "./navbar/NavbarDesktopNavigation";
import NavbarMegaMenu from "./navbar/NavbarMegaMenu";
import NavbarMobileDrawer from "./navbar/NavbarMobileDrawer";
import NavbarTopbar from "./navbar/NavbarTopbar";
import useNavbarCatalog from "./navbar/useNavbarCatalog";
import { resolveNavbarMobileStyle } from "./navbar/mobileNavigationStyle";
import { navbarBusinessNameColor, navbarLogoSurfaceStyle, navbarMobileOwner, navbarSurfaceStyle, navbarTopbarStyle } from "./navbar/navbarVisualOwnership";

const MEGA_MENU_CLOSE_DELAY = 220;

function optionalNavbarBorderRadius(value) {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(48, Math.max(0, number));
}

export default function Navbar({ promoBanner = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cartCount, openCart } = useCart();
  const { productsEnabled, ordersEnabled, gastronomicMenuEnabled, isCapabilityEnabled } = useProjectConfig();
  const membershipsEnabled = isCapabilityEnabled("memberships");
  const landingContext = useLanding();
  const landing = landingContext?.landing || landingContext || {};
  const { business, branding, theme, navigation, sections, contact, catalog } = landing;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [activeMegaButton, setActiveMegaButton] = useState(null);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [userAnchor, setUserAnchor] = useState(null);
  const [showBar, setShowBar] = useState(true);

  const lastScroll = useRef(0);
  const closeTimer = useRef(null);

  const homeUrl = navigation?.homeUrl || "/";
  const configuredProductsUrl = navigation?.productsUrl || "/productos";
  const defaultGastronomicNavigation = isCanonicalGastronomicCatalogNavigation(
    navigation,
    gastronomicMenuEnabled,
  );
  const aboutUrl = navigation?.aboutUrl || "#about";
  const highlightsUrl = navigation?.highlightsUrl || "#highlights";
  const contactUrl = navigation?.contactUrl || "/contact";
  const loginUrl = navigation?.loginUrl || "/login";
  const showNavbarLogo = navigation?.showLogo !== false;
  const logoUrl = showNavbarLogo ? resolveLandingMedia(branding?.logoUrl || theme?.logoUrl) : "";
  const compactLogoUrl = showNavbarLogo
    ? resolveLandingMedia(branding?.compactLogoUrl || branding?.logoUrl || theme?.compactLogoUrl || theme?.logoUrl)
    : "";
  const whatsappUrl = buildWhatsAppUrl(business?.whatsapp, contact?.whatsappMessage);
  const businessAddress = fullBusinessAddress(business);

  const navbarStyle = ["style-1", "style-2", "style-3"].includes(navigation?.style) ? navigation.style : "style-1";
  const commercialStyle = navbarStyle === "style-2";
  const darkStyle = navbarStyle === "style-3";
  const navbarBorderRadius = optionalNavbarBorderRadius(navigation?.borderRadius);
  const navbarSpacingScale = 1;
  const shellStyle = navbarSurfaceStyle(navigation, "shellStyle");
  const barStyle = navbarSurfaceStyle(navigation, "barStyle");
  const mobileShellStyle = navbarSurfaceStyle(navigation, "shellStyle", "mobile");
  const mobileBarStyle = navbarSurfaceStyle(navigation, "barStyle", "mobile");
  const topbarStyle = navbarTopbarStyle(navigation);
  const mobileNavbarStyle = resolveNavbarMobileStyle({ navigation });
  const mobileOpenButtonStyle = navbarMobileOwner(navigation, "openButtonStyle");
  const customMegaButtons = Array.isArray(navigation?.buttons)
    ? navigation.buttons.filter((button) => button?.behavior === "mega-menu")
    : [];
  const catalogEnabled = productsEnabled && (
    navigation?.showProductsLink !== false || customMegaButtons.length > 0
  );
  const inheritMegaSetting = (value, fallback) =>
    value && value !== "inherit" ? value : fallback;
  const megaMenuContent = inheritMegaSetting(
    activeMegaButton?.megaMenuContent,
    navigation?.megaMenuContent || "auto",
  );
  const megaMenuDisplay = inheritMegaSetting(
    activeMegaButton?.megaMenuDisplay,
    navigation?.megaMenuDisplay || "both",
  );
  const megaMenuHierarchy = inheritMegaSetting(
    activeMegaButton?.megaMenuHierarchy,
    navigation?.megaMenuHierarchy || "category-first",
  );
  const sectionLinkIsRenderable = (url, type) => {
    const target = String(url || "").trim();
    if (!target.startsWith("#")) return true;
    return hasVisibleLandingSegmentType(landing, type, productsEnabled);
  };
  const navigationSections = {
    ...(sections || {}),
    showAbout:
      sections?.showAbout !== false && sectionLinkIsRenderable(aboutUrl, "about"),
    showHighlights:
      sections?.showHighlights !== false &&
      sectionLinkIsRenderable(highlightsUrl, "highlights"),
    showContact:
      sections?.showContact !== false && sectionLinkIsRenderable(contactUrl, "contact"),
  };

  const {
    categories,
    latestProducts,
    resolvedMode,
    totalItems,
    hasProductItems,
    loading,
    error,
  } = useNavbarCatalog({
    enabled: catalogEnabled,
    errorText: navigation?.categoriesErrorText,
    catalogMode: catalog?.mode,
    menuContent: megaMenuContent,
    hierarchy: megaMenuHierarchy,
  });
  const productsUrl = catalogListPath(
    resolvedMode,
    defaultGastronomicNavigation ? "/productos" : configuredProductsUrl,
  );
  const productsLabel = catalogLabel(resolvedMode, navigation?.productsLabel);
  const catalogNavigation = {
    ...navigation,
    productsLabel,
    allProductsLabel:
      resolvedMode === "services" &&
      (navigation?.allProductsLabel === undefined || navigation.allProductsLabel === "Ver todos")
        ? "Ver todos los servicios"
        : navigation?.allProductsLabel,
  };
  const catalogOrdersEnabled = ordersEnabled && hasProductItems;
  const hasMobileNavigation = Boolean(
    (navigation?.showHomeLink !== false && String(navigation?.homeLabel || "").trim()) ||
    (navigation?.showProductsLink !== false && (productsEnabled || gastronomicMenuEnabled) && String(navigation?.productsLabel || "").trim()) ||
    (navigation?.showAboutLink !== false && sections?.showAbout !== false && String(navigation?.aboutLabel || "").trim()) ||
    (navigation?.showHighlightsLink !== false && sections?.showHighlights !== false && String(navigation?.highlightsLabel || "").trim()) ||
    (navigation?.showContactLink !== false && sections?.showContact !== false && String(navigation?.contactLabel || "").trim()) ||
    (navigation?.showCustomerAccess !== false && (user || String(navigation?.loginLabel || "").trim())) ||
    (Array.isArray(navigation?.buttons) && navigation.buttons.some((item) =>
      String(item?.label || "").trim() && (item?.behavior === "mega-menu" || String(item?.target || "").trim())
    ))
  );

  const goTo = (url, options) => openConfiguredUrl(url, navigate, options);
  const categoryUrl = (selection, legacySubcategory = "") => {
    const query = selection && typeof selection === "object"
      ? selection
      : { category: selection, subcategory: legacySubcategory };
    let target = productsUrl;
    if (query.category) target = appendConfiguredQuery(target, "categoria", query.category);
    if (query.subcategory) target = appendConfiguredQuery(target, "subcategoria", query.subcategory);
    return target;
  };

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY || 0;
      const delta = current - lastScroll.current;

      if (Math.abs(delta) > 9) {
        setShowBar(delta < 0 || current < 90);
        lastScroll.current = current;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMegaOpen(false);
    setActiveMegaButton(null);
    setDrawerOpen(false);
    setMobileCategoriesOpen(false);
    setUserAnchor(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMegaOpen(false);
        setActiveMegaButton(null);
        setDrawerOpen(false);
        setUserAnchor(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(closeTimer.current);
    },
    []
  );

  const openMega = () => {
    window.clearTimeout(closeTimer.current);
    setActiveMegaButton(null);
    setMegaOpen(true);
  };

  const openCustomMega = (button) => {
    window.clearTimeout(closeTimer.current);
    setActiveMegaButton(button || null);
    setMegaOpen(true);
  };

  const keepMegaOpen = () => {
    window.clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };

  const closeMegaSoon = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setMegaOpen(false);
      setActiveMegaButton(null);
    }, MEGA_MENU_CLOSE_DELAY);
  };

  const handleCustomNavbarButton = (button) => {
    if (button?.behavior === "mega-menu") {
      if (drawerOpen) {
        const currentKey = activeMegaButton?.id || null;
        const nextKey = button?.id || null;
        const sameButton = currentKey && nextKey && currentKey === nextKey;
        setActiveMegaButton(button);
        setMobileCategoriesOpen((current) => (sameButton ? !current : true));
        setMegaOpen(false);
      } else {
        openCustomMega(button);
      }
      return;
    }
    const behavior = button?.behavior === "link" ? "link" : "section";
    const target = String(button?.target || "").trim();
    if (!target) return;
    closeAllAndNavigate(target, { ensureHome: behavior === "section" });
  };

  const closeAllAndNavigate = (url, options = {}) => {
    setMegaOpen(false);
    setActiveMegaButton(null);
    setDrawerOpen(false);
    setMobileCategoriesOpen(false);

    const target = String(url || "").trim();
    const ensureHome = options.ensureHome === true && target.startsWith("#");
    if (ensureHome && location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => openConfiguredUrl(target, navigate), 80);
      return;
    }

    goTo(target);
  };

  const handleLogout = async () => {
    setUserAnchor(null);
    setDrawerOpen(false);
    await Promise.resolve(logout?.());
    goTo(homeUrl);
  };

  if (location.pathname.startsWith("/admin")) return null;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        data-landing-section="navbar"
        data-navbar-style={navbarStyle}
        sx={{
          zIndex: 1200,
          bgcolor: { xs: mobileShellStyle.backgroundColor, md: shellStyle.backgroundColor },
          borderBottom: "1px solid",
          borderColor: { xs: mobileShellStyle.borderColor, md: shellStyle.borderColor },
          backdropFilter: navbarStyle === "style-1" ? "blur(18px) saturate(145%)" : "none",
          WebkitBackdropFilter:
            navbarStyle === "style-1" ? "blur(18px) saturate(145%)" : "none",
          boxShadow: { xs: mobileShellStyle.boxShadow, md: shellStyle.boxShadow },
          transform:
            showBar || megaOpen || drawerOpen ? "translateY(0)" : "translateY(-115%)",
          transition: "transform .28s ease, box-shadow .28s ease",
          ...(navbarBorderRadius !== null
            ? { borderRadius: `${navbarBorderRadius}px` }
            : {}),
        }}
      >
        {promoBanner}
        <NavbarTopbar
          visible={commercialStyle}
          style={topbarStyle}
          borderRadius={navbarBorderRadius}
          business={business}
          businessAddress={businessAddress}
          whatsappUrl={whatsappUrl}
          showSocialLinks={navigation?.showSocialLinks !== false}
        />

        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 72 * navbarSpacingScale, md: (commercialStyle ? 82 : 78) * navbarSpacingScale },
            width: "100%",
            maxWidth: 1440,
            mx: "auto",
            px: { xs: 1.6 * navbarSpacingScale, sm: 2.5 * navbarSpacingScale, md: 4 * navbarSpacingScale, lg: 5 * navbarSpacingScale },
            bgcolor: { xs: mobileBarStyle.backgroundColor, md: barStyle.backgroundColor },
            border: "1px solid",
            borderColor: { xs: mobileBarStyle.borderColor, md: barStyle.borderColor },
            boxShadow: { xs: mobileBarStyle.boxShadow, md: barStyle.boxShadow },
            ...(navbarBorderRadius !== null ? { borderRadius: `${navbarBorderRadius}px` } : {}),
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              width: "100%",
              alignItems: "center",
              gap: 0.85,
            }}
          >
            <NavbarBrand
              compact
              surfaceStyle={navbarLogoSurfaceStyle(navigation, "mobile")}
              businessName={business?.name}
              homeLabel={navigation?.homeLabel}
              logoUrl={logoUrl}
              compactLogoUrl={compactLogoUrl}
              maxLogoHeight={mobileNavbarStyle.logoMaxHeight}
              logoColor={mobileNavbarStyle.logoColor}
              textColor={navbarBusinessNameColor(navigation, "mobile")}
              onClick={() => closeAllAndNavigate(homeUrl)}
            />
            <Box sx={{ flex: 1 }} />
            <NavbarActions
              compact
              user={user}
              navigation={catalogNavigation}
              ordersEnabled={catalogOrdersEnabled}
              cartCount={cartCount}
              onOpenCart={openCart}
              onOpenAccount={(event) => setUserAnchor(event.currentTarget)}
              onLogin={() => closeAllAndNavigate(loginUrl)}
            />
            {hasMobileNavigation ? <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label={navigation?.menuAriaLabel ?? "Abrir menú"}
              sx={{
                width: mobileOpenButtonStyle.size,
                height: mobileOpenButtonStyle.size,
                color: mobileOpenButtonStyle.color,
                border: "1px solid",
                borderColor: mobileOpenButtonStyle.borderColor,
                bgcolor: mobileOpenButtonStyle.backgroundColor,
                "&:hover": {
                  color: mobileOpenButtonStyle.hoverColor,
                  bgcolor: mobileOpenButtonStyle.hoverBackgroundColor,
                  borderColor: mobileOpenButtonStyle.hoverBorderColor,
                },
                "&:focus-visible": {
                  outline: `3px solid ${mobileOpenButtonStyle.focusColor}`,
                  outlineOffset: 2,
                },
              }}
            >
              <MenuRoundedIcon />
            </IconButton> : null}
          </Box>

          {darkStyle ? (
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                width: "100%",
                gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)",
                alignItems: "center",
                gap: 2,
              }}
            >
              <NavbarDesktopNavigation
                location={location}
                productsEnabled={productsEnabled}
                gastronomicMenuEnabled={gastronomicMenuEnabled}
                sections={navigationSections}
                navigation={catalogNavigation}
                megaOpen={megaOpen}
                onHome={() => closeAllAndNavigate(homeUrl)}
                onProducts={() => closeAllAndNavigate(productsUrl)}
                onGastronomicMenu={() => closeAllAndNavigate("/carta")}
                onAbout={() => closeAllAndNavigate(aboutUrl, { ensureHome: true })}
                onHighlights={() =>
                  closeAllAndNavigate(highlightsUrl, { ensureHome: true })
                }
                onContact={() => closeAllAndNavigate(contactUrl)}
                onCustomButton={handleCustomNavbarButton}
                onOpenMega={openMega}
                onOpenCustomMega={openCustomMega}
                onCloseMegaSoon={closeMegaSoon}
              />

              <NavbarBrand
                surfaceStyle={navbarLogoSurfaceStyle(navigation, "desktop")}
                businessName={business?.name}
                homeLabel={navigation?.homeLabel}
                logoUrl={logoUrl}
                compactLogoUrl={compactLogoUrl}
                maxLogoHeight={navigation?.logoMaxHeight}
                logoColor={navigation?.logoColor || ""}
                textColor={navbarBusinessNameColor(navigation, "desktop")}
                onClick={() => closeAllAndNavigate(homeUrl)}
              />

              <Stack direction="row" justifyContent="flex-end">
                <NavbarActions
                  user={user}
                  navigation={catalogNavigation}
                  ordersEnabled={catalogOrdersEnabled}
                  cartCount={cartCount}
                  onOpenCart={openCart}
                  onOpenAccount={(event) => setUserAnchor(event.currentTarget)}
                  onLogin={() => closeAllAndNavigate(loginUrl)}
                />
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                width: "100%",
                alignItems: "center",
                gap: { md: 1.6, lg: 2.4 },
              }}
            >
              <NavbarBrand
                surfaceStyle={navbarLogoSurfaceStyle(navigation, "desktop")}
                businessName={business?.name}
                homeLabel={navigation?.homeLabel}
                logoUrl={logoUrl}
                compactLogoUrl={compactLogoUrl}
                maxLogoHeight={navigation?.logoMaxHeight}
                logoColor={navigation?.logoColor || ""}
                textColor={navbarBusinessNameColor(navigation, "desktop")}
                onClick={() => closeAllAndNavigate(homeUrl)}
              />

              <Box sx={{ flex: 1 }} />

              <NavbarDesktopNavigation
                location={location}
                productsEnabled={productsEnabled}
                gastronomicMenuEnabled={gastronomicMenuEnabled}
                sections={navigationSections}
                navigation={catalogNavigation}
                megaOpen={megaOpen}
                onHome={() => closeAllAndNavigate(homeUrl)}
                onProducts={() => closeAllAndNavigate(productsUrl)}
                onGastronomicMenu={() => closeAllAndNavigate("/carta")}
                onAbout={() => closeAllAndNavigate(aboutUrl, { ensureHome: true })}
                onHighlights={() =>
                  closeAllAndNavigate(highlightsUrl, { ensureHome: true })
                }
                onContact={() => closeAllAndNavigate(contactUrl)}
                onCustomButton={handleCustomNavbarButton}
                onOpenMega={openMega}
                onOpenCustomMega={openCustomMega}
                onCloseMegaSoon={closeMegaSoon}
              />

              <NavbarActions
                user={user}
                navigation={catalogNavigation}
                ordersEnabled={catalogOrdersEnabled}
                cartCount={cartCount}
                onOpenCart={openCart}
                onOpenAccount={(event) => setUserAnchor(event.currentTarget)}
                onLogin={() => closeAllAndNavigate(loginUrl)}
              />
            </Box>
          )}
        </Toolbar>

        {catalogEnabled ? (
          <NavbarMegaMenu
            open={megaOpen}
            navigation={catalogNavigation}
            displayMode={megaMenuDisplay}
            categories={categories}
            latestProducts={latestProducts}
            productCount={totalItems}
            catalogMode={resolvedMode}
            loading={loading}
            error={error}
            onMouseEnter={keepMegaOpen}
            onMouseLeave={closeMegaSoon}
            onAllProducts={() => closeAllAndNavigate(productsUrl)}
            onCategory={(selection) => closeAllAndNavigate(categoryUrl(selection))}
            onSubcategory={(selection, subcategory) =>
              closeAllAndNavigate(categoryUrl(selection, subcategory))
            }
            onProduct={(path) => closeAllAndNavigate(path)}
          />
        ) : null}
      </AppBar>

      <NavbarAccountMenu
        anchorEl={userAnchor}
        user={user}
        navigation={catalogNavigation}
        membershipsEnabled={membershipsEnabled}
        onClose={() => setUserAnchor(null)}
        onAppointments={() => closeAllAndNavigate("/mi-cuenta/turnos")}
        onMemberships={() => closeAllAndNavigate("/mi-cuenta/mensualidades")}
        onLogout={handleLogout}
      />

      <NavbarMobileDrawer
        open={drawerOpen}
        location={location}
        business={business}
        logoUrl={logoUrl}
        compactLogoUrl={compactLogoUrl}
        productsEnabled={productsEnabled}
        gastronomicMenuEnabled={gastronomicMenuEnabled}
        sections={navigationSections}
        navigation={catalogNavigation}
        mobileStyle={mobileNavbarStyle}
        user={user}
        categories={categories}
        latestProducts={latestProducts}
        productCount={totalItems}
        displayMode={megaMenuDisplay}
        activeMegaButtonId={activeMegaButton?.id || null}
        loading={loading}
        error={error}
        categoriesOpen={mobileCategoriesOpen}
        whatsappUrl={whatsappUrl}
        onClose={() => setDrawerOpen(false)}
        onHome={() => closeAllAndNavigate(homeUrl)}
        onProducts={() => closeAllAndNavigate(productsUrl)}
        onGastronomicMenu={() => closeAllAndNavigate("/carta")}
        onAbout={() => closeAllAndNavigate(aboutUrl, { ensureHome: true })}
        onHighlights={() =>
          closeAllAndNavigate(highlightsUrl, { ensureHome: true })
        }
        onContact={() => closeAllAndNavigate(contactUrl)}
        onCustomButton={handleCustomNavbarButton}
        onItem={(path) => closeAllAndNavigate(path)}
        onLogin={() => closeAllAndNavigate(loginUrl)}
        membershipsEnabled={membershipsEnabled}
        onAppointments={() => closeAllAndNavigate("/mi-cuenta/turnos")}
        onMemberships={() => closeAllAndNavigate("/mi-cuenta/mensualidades")}
        onLogout={handleLogout}
        onCategory={(selection) => closeAllAndNavigate(categoryUrl(selection))}
        onSubcategory={(selection, subcategory) =>
          closeAllAndNavigate(categoryUrl(selection, subcategory))
        }
        onToggleCategories={() => {
          setActiveMegaButton(null);
          setMobileCategoriesOpen((current) => !current);
        }}
      />
    </>
  );
}
